import React, { ButtonHTMLAttributes, forwardRef } from 'react'
import { cn } from '../../utils'
import { type Icon, RightArrow } from '../icons'
import { Loader } from '../..'

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
	variant?: 'solid' | 'outline'
	color?: 'primary' | 'secondary' | 'tertiary' | 'adaptive' | 'link'
	size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'icon'
	icon?: Icon
	noIcon?: boolean
	title?: string
	iconBefore?: boolean
	isLoading?: boolean
	asChild?: boolean
}

interface buttonVariantMapsProps {
	[rounded: string]: string
}

interface buttonSizeMapsProps {
	[size: string]: string
}

interface buttonColorMapsProps {
	[color: string]: string
}

const buttonVariantMaps: buttonVariantMapsProps = {
	solid: 'disabled:bg-gray-200 disabled:text-gray-400',
	outline:
		'!bg-transparent border disabled:border-gray-400 hover:!bg-gray-100/50',
}

const buttonSizeMaps: buttonSizeMapsProps = {
	xs: 'py-1 px-2.5 leading-4 text-xs',
	sm: 'py-2 px-4 leading-5 text-xs',
	md: 'py-3 px-6 leading-6',
	lg: 'py-4 px-6 leading-6 lg:px-8 lg:leading-8 lg:text-md',
	icon: 'w-8 h-8 p-0',
}

const buttonColorMaps: buttonColorMapsProps = {
	primary:
		'bg-primary text-primary-foreground border-primary-foreground stroke-primary-foreground hover:bg-primary/80',
	secondary:
		'bg-foreground text-background border-white stroke-background hover:bg-foreground/80',
	tertiary:
		'bg-background text-foreground border-foreground stroke-foreground hover:bg-background/80',
	adaptive:
		'text-foreground border-foreground stroke-foreground dark:text-primary-foreground',
	link: 'hover:font-bold stroke-foreground',
}

const base =
	'flex items-center rounded-full cursor-pointer gap-2 disabled:text-gray-400 disabled:cursor-default select-none duration-200'

const defaultVariants = {
	variant: 'solid',
	color: 'primary',
	size: 'md',
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
	(
		{
			children,
			variant,
			size,
			color,
			disabled,
			icon,
			noIcon,
			iconBefore,
			className = '',
			title,
			isLoading,
			asChild,
			...rest
		},
		ref,
	) => {
		const buttonClasses = cn(
			base,
			color ? buttonColorMaps[color] : buttonColorMaps[defaultVariants.color],
			variant
				? buttonVariantMaps[variant]
				: buttonVariantMaps[defaultVariants.variant],
			size ? buttonSizeMaps[size] : buttonSizeMaps[defaultVariants.size],
			{ 'justify-center': noIcon },
			// full rounded :
			{
				'!p-[3px]': !children && (size || defaultVariants.size) === 'xs',
			},
			{
				'!p-[7px]': !children && (size || defaultVariants.size) === 'sm',
			},
			{
				'!p-3':
					!children &&
					((size || defaultVariants.size) === 'md' ||
						(size || defaultVariants.size) === 'lg'),
			},
			{ '!p-4': !children && (size || defaultVariants.size) === 'xl' },
			className,
		)

		const buttonContent = (
			<>
				{!iconBefore && children}
				{!icon && !noIcon && (
					<RightArrow
						disabled={!!disabled}
						xs={size === 'xs' || size === 'sm'}
					/>
				)}
				{icon &&
					!isLoading &&
					React.createElement(icon, {
						disabled,
						xs: size === 'xs' || size === 'sm',
					})}
				{icon && isLoading && <Loader className="w-6 h-6" />}
				{iconBefore && children}
			</>
		)

		if (asChild && React.isValidElement(children)) {
			return React.cloneElement(children as React.ReactElement<any>, {
				...rest,
				className: cn(
					buttonClasses,
					(children as React.ReactElement<any>).props.className,
				),
				ref,
				disabled,
				title,
				children: buttonContent,
			})
		}

		return (
			<button
				ref={ref}
				className={buttonClasses}
				title={title}
				disabled={disabled}
				{...rest}
			>
				{buttonContent}
			</button>
		)
	},
)

Button.displayName = 'Button'
